import { addDays } from "date-fns/addDays";
import { format } from "date-fns/format";
import { isValid } from "date-fns/isValid";
import { parse } from "date-fns/parse";
import { parseISO } from "date-fns/parseISO";

import { TDateISO } from "~/@types/TDateISO";
import activeLocaleTag from "~/helpers/activeLocale";

export const dayFormat = {
	day: "numeric",
	month: "short"
} as const;

export const dayWithWeekdayFormat = {
	day: "numeric",
	month: "short",
	weekday: "short"
} as const;

const timeFormat = {
	hour: "numeric",
	minute: "2-digit"
} as const;

const yearFormat = {
	year: "numeric"
} as const;

const dateFormatOptions: Intl.DateTimeFormatOptions = {
	...dayFormat,
	...yearFormat
};

const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
	...dayFormat,
	...timeFormat,
	...yearFormat
};

const dayTimeFormatOptions: Intl.DateTimeFormatOptions = {
	...dayFormat,
	...timeFormat
};

export const getBrowserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

// Intl.supportedValuesOf lands in the lib.dom types only from TS 5.1's ES2023 lib; the runtime has it.
type IntlWithSupportedValues = typeof Intl & { supportedValuesOf?: (key: "timeZone") => string[] };

export const listTimeZones = (): string[] => {
	const supportedValuesOf = (Intl as IntlWithSupportedValues).supportedValuesOf;
	return supportedValuesOf ? supportedValuesOf("timeZone") : [getBrowserTimezone()];
};

// wsapi's cycleCountHistory reads only the last 7 days when `since` is omitted.
export const ALL_TIME_CYCLE_COUNT_SINCE = "1970-01-01T00:00:00.000Z" as TDateISO;

// .NET DateTime.MinValue (0001-01-01) arrives from unset read-model dates; treat it as no date rather than rendering "Dec 31, 1".
export const isEpochSentinelDate = (date: string): boolean => {
	const parsed = new Date(date);
	return !Number.isNaN(parsed.getTime()) && parsed.getUTCFullYear() <= 1;
};

export const stringFormatDateTime = (date: string): string => {
	if (isEpochSentinelDate(date)) return "";
	return new Date(date).toLocaleString(activeLocaleTag(), dateTimeFormatOptions);
};

export const stringFormatDayTime = (date: string): string => {
	return new Date(date).toLocaleString(activeLocaleTag(), dayTimeFormatOptions);
};

export const stringFormatTime = (date: string): string => {
	return new Date(date).toLocaleTimeString(activeLocaleTag(), timeFormat);
};

const dateOnlyIsoPattern = /^\d{4}-\d{2}-\d{2}(T00:00:00(?:\.0+)?(?:Z|\+00:00))?$/;

// Force UTC for canonical date-only ISO so the user's calendar day doesn't shift west in negative-offset timezones.
export const stringFormatDate = (date: string): string => {
	if (isEpochSentinelDate(date)) return "";
	const options = dateOnlyIsoPattern.test(date) ? { ...dateFormatOptions, timeZone: "UTC" } : dateFormatOptions;
	return new Date(date).toLocaleDateString(activeLocaleTag(), options);
};

export const stringFormatUTCDate = (date: string): string => {
	const options = {
		...dateFormatOptions,
		timeZone: "UTC"
	};
	return new Date(date).toLocaleDateString(activeLocaleTag(), options);
};

export const stringFormatUnixTime = (date: string): number => {
	return Math.floor(new Date(date).getTime() / 1000);
};

export const timestampFormatDateTime = (ts: number, mult: number = 1): string => {
	return new Date(ts * mult).toLocaleString(activeLocaleTag());
};

export const checkValidDateISO = (date: string): boolean => {
	if (date.match(/^\d{4}-\d{2}-\d{2}T/)) {
		return true;
	}
	return false;
};

interface DateFormatRule {
	format: string;
	referenceDate?: Date;
	shape: RegExp;
}

export const STRICT_ISO_DATE: DateFormatRule = { format: "yyyy-MM-dd", shape: /^\d{4}-\d{2}-\d{2}$/ };
const LENIENT_ISO_DATE: DateFormatRule = { format: "yyyy-M-d", shape: /^\d{4}-\d{1,2}-\d{1,2}$/ };
const US_DATE: DateFormatRule = { format: "M/d/yyyy", shape: /^\d{1,2}\/\d{1,2}\/\d{4}$/ };
const SUPPORTED_DATE_RULES = [LENIENT_ISO_DATE, US_DATE];

// date-fns `yyyy` also parses 1–3 digit years, so the shape regex pins the digit counts.
const parseDateInFormat = (value: string, { format, referenceDate, shape }: DateFormatRule): Date | null => {
	if (!shape.test(value)) return null;
	const date = parse(value, format, referenceDate ?? new Date());
	return isValid(date) ? date : null;
};

const parseSupportedDate = (value: string): Date | null =>
	SUPPORTED_DATE_RULES.map((rule) => parseDateInFormat(value, rule)).find((date) => date !== null) ?? null;

const IMPORT_DATE_FORMAT = "MM/dd/yyyy";
const TIME_OF_DAY_SUFFIX = /[ T]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?(\s?[AP]M)?$/i;
const EXCEL_DATE_NUMBER = /^(\d{5})(\.\d+)?$/;
// Excel's day 1 is 1900-01-01 but it counts a nonexistent 1900-02-29, so modern serials count from 1899-12-30.
const EXCEL_EPOCH = new Date(1899, 11, 30);
const FIRST_EXCEL_DAY_OF_2000 = 36526;
const LAST_EXCEL_DAY_OF_2099 = 73050;
// date-fns maps a `yy` year into the 100 years ending 50 years after the reference date, so 2050 gives 2000–2099.
const TWO_DIGIT_YEAR_REFERENCE = new Date(2050, 0, 1);

const COMPACT_DATE: DateFormatRule = { format: "yyyyMMdd", shape: /^\d{8}$/ };
const US_SHORT_YEAR_DATE: DateFormatRule = {
	format: "M/d/yy",
	referenceDate: TWO_DIGIT_YEAR_REFERENCE,
	shape: /^\d{1,2}\/\d{1,2}\/\d{2}$/
};
const EXCEL_SHORT_DATE: DateFormatRule = {
	format: "d-MMM-yy",
	referenceDate: TWO_DIGIT_YEAR_REFERENCE,
	shape: /^\d{1,2}-[a-z]{3}-\d{2}$/i
};
const EXCEL_SHORT_DATE_FULL_YEAR: DateFormatRule = { format: "d-MMM-yyyy", shape: /^\d{1,2}-[a-z]{3}-\d{4}$/i };

const IMPORT_DATE_RULES = [
	US_DATE,
	LENIENT_ISO_DATE,
	COMPACT_DATE,
	US_SHORT_YEAR_DATE,
	EXCEL_SHORT_DATE,
	EXCEL_SHORT_DATE_FULL_YEAR
];

const parseExcelDateNumber = (dateText: string): Date | null => {
	const wholeDays = EXCEL_DATE_NUMBER.exec(dateText)?.[1];
	if (!wholeDays) return null;
	const serial = Number(wholeDays);
	if (serial < FIRST_EXCEL_DAY_OF_2000 || serial > LAST_EXCEL_DAY_OF_2099) return null;
	return addDays(EXCEL_EPOCH, serial);
};

const parseImportDate = (dateText: string): Date | null =>
	parseExcelDateNumber(dateText) ??
	IMPORT_DATE_RULES.map((rule) => parseDateInFormat(dateText, rule)).find((date) => date !== null) ??
	null;

export const normalizeImportDate = (rawDateText: string): string => {
	const date = parseImportDate(rawDateText.trim().replace(TIME_OF_DAY_SUFFIX, ""));
	return date ? format(date, IMPORT_DATE_FORMAT) : rawDateText;
};

export const isValidDateInFormat = (value: string, rule: DateFormatRule): boolean =>
	parseDateInFormat(value, rule) !== null;

export const isValidDateFormat = (dateString: string): boolean => parseSupportedDate(dateString) !== null;

export const convertToIsoDateTime = (dateStr?: string | null): string | undefined => {
	const trimmed = dateStr?.trim();
	if (!trimmed) {
		return undefined;
	}
	if (checkValidDateISO(trimmed)) {
		return isValid(parseISO(trimmed)) ? trimmed : undefined;
	}
	const date = parseSupportedDate(trimmed);
	return date ? toLocalDateTimeIso(date) : undefined;
};

/**
 * Extracts the local date from a Date object as a YYYY-MM-DD string.
 * Unlike Date.toISOString() which converts to UTC (potentially shifting the calendar date),
 * this uses the local date components to preserve the date the user actually selected.
 *
 * Use this for date-only fields (ship dates, transfer dates, etc.) where the time component
 * is irrelevant and UTC conversion would cause off-by-one date errors for users in
 * negative-UTC timezones (e.g., US time zones in the evening).
 *
 * @param date - The Date object to extract the local date from
 * @returns Date string in YYYY-MM-DD format based on local time
 */
export const toLocalDateIso = (date: Date): string => {
	const year = String(date.getFullYear()).padStart(4, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/**
 * Converts a Date to a TDateISO string using local date components.
 * Returns "YYYY-MM-DDT00:00:00.000Z" preserving the local calendar date.
 * Use this when sending date-only values to the API as DateTime scalars.
 */
export const toLocalDateTimeIso = (date: Date): TDateISO => `${toLocalDateIso(date)}T00:00:00.000Z` as TDateISO;

export const toLocalTimestampIso = (date: Date): TDateISO => {
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${toLocalDateIso(date)}T${hours}:${minutes}:00.000Z` as TDateISO;
};

export const ymdToStartOfDayIso = (ymd: string): TDateISO => `${ymd}T00:00:00.000Z` as TDateISO;

/**
 * Normalizes a TDateISO value to the start of its calendar day ("YYYY-MM-DDT00:00:00.000Z"),
 * keeping the YYYY-MM-DD part the UI displays. Use when persisting date-only range starts so
 * a value carrying a time-of-day (e.g. a creation-instant stamp) cannot exclude same-day data.
 */
export const toStartOfDayIso = (value: TDateISO): TDateISO => `${value.slice(0, 10)}T00:00:00.000Z` as TDateISO;

/**
 * Normalizes a TDateISO value to the end of its calendar day ("YYYY-MM-DDT23:59:59.999Z").
 * Counterpart of toStartOfDayIso for date-only range ends.
 */
export const toEndOfDayIso = (value: TDateISO): TDateISO => `${value.slice(0, 10)}T23:59:59.999Z` as TDateISO;

/**
 * Parses a TDateISO string into a Date object using its YYYY-MM-DD components
 * as local calendar values. Avoids the UTC offset that `new Date(isoString)`
 * would apply, so date pickers display the same day the user originally selected.
 */
export const parseDateISOToLocal = (value: TDateISO): Date => {
	const parts = value.slice(0, 10).split("-").map(Number);
	return new Date(parts[0], parts[1] - 1, parts[2]);
};

export const timeStringToTimestampIso = (time: string): TDateISO => `2000-01-01T${time}:00.000Z` as TDateISO;

export const timestampIsoToTimeString = (value: TDateISO): string => value.slice(11, 16);

export const parseTimestampISOToLocal = (value: TDateISO): Date => {
	const date = parseDateISOToLocal(value);
	const [hours, minutes] = value.slice(11, 16).split(":").map(Number);
	date.setHours(hours || 0, minutes || 0);
	return date;
};

/**
 * Parses a YYYY-MM-DD calendar string from a date input into a local Date
 * (midnight in the user's timezone). Returns null for malformed input. Use this
 * to round-trip <input type="date"> values without UTC drift.
 */
export const parseYmdToLocalDate = (ymd: string): Date | null => {
	const [y, m, d] = ymd.split("-").map(Number);
	if (!y || !m || !d) {
		return null;
	}
	return new Date(y, m - 1, d);
};

/**
 * Formats a YYYY-MM-DD calendar string as a localized date (e.g. "May 11, 2026").
 * Returns the original input unchanged when it is not a valid Y-M-D string.
 */
export const formatYmdLocal = (ymd: string): string => {
	const date = parseYmdToLocalDate(ymd);
	return date === null
		? ymd
		: date.toLocaleDateString(activeLocaleTag(), { day: "numeric", month: "short", year: "numeric" });
};

// MM/DD/YYYY matches the grid display so the CSV survives the Excel US-locale round-trip
export const formatMmDdYyyy = (value: string): string => {
	const date = parseYmdToLocalDate(value.slice(0, 10));
	if (date === null) {
		return "";
	}
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${month}/${day}/${date.getFullYear()}`;
};

export const formatDateLabel = (date: Date, options: Intl.DateTimeFormatOptions): string =>
	date.toLocaleDateString(activeLocaleTag(), options);

export const formatTimestampTime = (timestamp: string): string => {
	const date = new Date(timestamp);
	return date.toLocaleTimeString(activeLocaleTag(), { hour: "numeric", hour12: true, minute: "2-digit" });
};

export const formatTimestampTimeWithSeconds = (timestamp: string): string => {
	const date = new Date(timestamp);
	return date.toLocaleTimeString(activeLocaleTag(), {
		hour: "numeric",
		hour12: true,
		minute: "2-digit",
		second: "2-digit"
	});
};

export const formatDuration = (ms: number): string => {
	if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
	return `${ms}ms`;
};

export type RelativeTimestampUnit = "day" | "hour" | "minute";

type RelativeBucket =
	| { kind: "justNow" }
	| { kind: "relative"; count: number; unit: RelativeTimestampUnit }
	| { kind: "date" };

const bucketRelativeDiff = (diffMs: number): RelativeBucket => {
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMins / 60);
	const diffDays = Math.floor(diffHours / 24);

	if (diffMins < 1) return { kind: "justNow" };
	if (diffMins < 60) return { kind: "relative", count: diffMins, unit: "minute" };
	if (diffHours < 24) return { kind: "relative", count: diffHours, unit: "hour" };
	if (diffDays < 7) return { kind: "relative", count: diffDays, unit: "day" };
	return { kind: "date" };
};

export type RelativeTimestampParts =
	| { kind: "blank" }
	| { kind: "justNow" }
	| { kind: "relative"; count: number; unit: RelativeTimestampUnit }
	| { kind: "date"; date: Date };

export const relativeTimestampParts = (timestamp: string, now: Date = new Date()): RelativeTimestampParts => {
	if (isEpochSentinelDate(timestamp)) return { kind: "blank" };
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return { kind: "blank" };
	const bucket = bucketRelativeDiff(now.getTime() - date.getTime());
	return bucket.kind === "date" ? { kind: "date", date } : bucket;
};
